import { useMemo } from "react";
import type { SetURLSearchParams } from "react-router";
import { type DocumentSummary } from "~/api/types";
import { useTags } from "~/api/swr/useTags";
import { useTiers } from "~/api/swr/useTiers";
import { useCategories } from "~/api/swr/useCategories";
import { DocumentStatus } from "~/config/types";
import {
  dateFilterKeys,
  nullableDateFilterKeys,
  FilterOperatorEnum,
  type FilterCondition,
  type DateFilterKey,
  type FilterField,
  type FilterOperator,
  type NullableDateFilterKey,
  isFilterFieldValueValid,
  isNullableDateFilterKey,
  type MultiSelectFilterKey,
  parseFilters,
  type SelectFieldData,
} from "../helpers/documentFilters";
import { createDocumentFilterUrlState } from "~/helpers/documentFilterUrlState";
import { printFullName } from "../helpers/user";
import { getNoTierLabel, NO_TIER_VALUE } from "~/helpers/tier";
import { useTranslation } from "react-i18next";

interface UseDocumentFilterParams {
  documents: DocumentSummary[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  multiSelectFields?: readonly MultiSelectFilterKey[];
  dateFields?: readonly DateFilterKey[];
}

const libraryMultiSelectFilterKeys = [
  "archiveState",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
] as const satisfies readonly MultiSelectFilterKey[];

export interface DocumentFiltersModel {
  dateFilterKeys: readonly DateFilterKey[];
  multiSelectFilterKeys: readonly MultiSelectFilterKey[];
  nullableDateFilterKeys: typeof nullableDateFilterKeys;
  multiSelectFieldData: Record<MultiSelectFilterKey, SelectFieldData>;
  activeFilters: FilterCondition[];
  getFilterValue: (
    field: FilterField,
    operator: FilterOperator,
  ) => string | undefined;
  setFilterValue: (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => void;
  getInValues: (field: MultiSelectFilterKey) => string[];
  setInValues: (field: MultiSelectFilterKey, values: string[]) => void;
  isNullFilterActive: (field: NullableDateFilterKey) => boolean;
  setNullFilter: (field: NullableDateFilterKey, active: boolean) => void;
  isDateRangeInvalid: (filterKey: DateFilterKey) => boolean;
  removeFilter: (field: FilterField) => void;
}

type ActiveFilterValuesByField = Partial<
  Record<FilterField, Partial<Record<FilterOperator, string>>>
>;
type ActiveMultiSelectValuesByField = Partial<
  Record<MultiSelectFilterKey, string[]>
>;

const buildSelectFieldData = (
  entries: Array<[string, string]>,
): SelectFieldData => {
  const labelsByValue = entries.reduce<Record<string, string>>(
    (acc, [value, label]) => {
      acc[value] = label;
      return acc;
    },
    {},
  );

  return {
    labelsByValue,
    values: Object.keys(labelsByValue).sort((left, right) =>
      labelsByValue[left]!.localeCompare(labelsByValue[right]!, undefined, {
        numeric: true,
      }),
    ),
  };
};

export const useDocumentFilters = ({
  documents,
  searchParams,
  setSearchParams,
  multiSelectFields = libraryMultiSelectFilterKeys,
  dateFields = dateFilterKeys,
}: UseDocumentFilterParams): DocumentFiltersModel => {
  const { data: tags } = useTags();
  const { data: tiers } = useTiers();
  const { data: categories } = useCategories();
  const { t } = useTranslation();

  const filterUrlState = useMemo(
    () =>
      createDocumentFilterUrlState(searchParams, {
        multiSelectFields,
        dateFields,
      }),
    [searchParams, multiSelectFields, dateFields],
  );
  const activeFilters = useMemo(
    () =>
      parseFilters(searchParams, {
        multiSelectFields,
        dateFields,
      }),
    [searchParams, multiSelectFields, dateFields],
  );
  const activeFilterValuesByField = useMemo(
    () =>
      activeFilters.reduce<ActiveFilterValuesByField>((acc, filter) => {
        if (typeof filter.value !== "string") {
          return acc;
        }

        acc[filter.field] = {
          ...(acc[filter.field] ?? {}),
          [filter.operator]: filter.value,
        };
        return acc;
      }, {}),
    [activeFilters],
  );
  const activeMultiSelectValuesByField = useMemo(
    () =>
      activeFilters.reduce<ActiveMultiSelectValuesByField>((acc, filter) => {
        if (
          !(multiSelectFields as readonly string[]).includes(filter.field) ||
          (filter.operator !== FilterOperatorEnum.eq &&
            filter.operator !== FilterOperatorEnum.in)
        ) {
          return acc;
        }

        acc[filter.field as MultiSelectFilterKey] = Array.isArray(filter.value)
          ? filter.value
          : [filter.value];
        return acc;
      }, {}),
    [activeFilters, multiSelectFields],
  );

  const multiSelectFieldData = useMemo<
    Record<MultiSelectFilterKey, SelectFieldData>
  >(() => {
    const tierOptions = tiers ?? [];
    const categoryOptions = categories ?? [];
    const tagOptions = tags ?? [];

    return {
      archiveState: {
        values: [DocumentStatus.active, DocumentStatus.archived],
        labelsByValue: {
          [DocumentStatus.active]: t(($) => $.commons.document.statuses.active),
          [DocumentStatus.archived]: t(
            ($) => $.commons.document.statuses.archived,
          ),
        },
      },
      tier: [
        ...tierOptions.map((tier) => ({
          value: tier.id.toString(),
          label: tier.title,
        })),
        { value: NO_TIER_VALUE, label: getNoTierLabel(t) },
      ].reduce<SelectFieldData>(
        (acc, option) => {
          acc.values.push(option.value);
          acc.labelsByValue[option.value] = option.label;
          return acc;
        },
        { values: [], labelsByValue: {} },
      ),
      category: buildSelectFieldData(
        categoryOptions.map((category) => [
          category.id.toString(),
          category.title,
        ]),
      ),
      tag: buildSelectFieldData(
        tagOptions.map((tag) => [tag.id.toString(), tag.title]),
      ),
      owner: buildSelectFieldData(
        documents.map((doc) => [
          doc.owner.userId.toString(),
          printFullName(doc.owner),
        ]),
      ),
      publisher: buildSelectFieldData(
        documents.flatMap((doc) => {
          const publisher = doc.publisher;

          if (!publisher) {
            return [];
          }

          return [[publisher.userId.toString(), printFullName(publisher)]];
        }),
      ),
    };
  }, [documents, tags, tiers, categories, t]);

  const setFilters = (nextFilters: FilterCondition[]) => {
    setSearchParams(filterUrlState.replaceDocumentFilterParams(nextFilters));
  };

  const getFilterValue = (field: FilterField, operator: FilterOperator) =>
    activeFilterValuesByField[field]?.[operator];

  const setFilterValue = (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => {
    let nextFilters = activeFilters.filter(
      (filter) => !(filter.field === field && filter.operator === operator),
    );

    if (
      isNullableDateFilterKey(field) &&
      operator !== FilterOperatorEnum.isNull
    ) {
      nextFilters = nextFilters.filter(
        (filter) =>
          !(
            filter.field === field &&
            filter.operator === FilterOperatorEnum.isNull
          ),
      );
    }

    if (value && value.length > 0) {
      if (!isFilterFieldValueValid(field, value)) {
        return;
      }

      nextFilters.push({
        field,
        operator,
        value,
      });
    }

    setFilters(nextFilters);
  };

  const getInValues = (field: MultiSelectFilterKey) =>
    activeMultiSelectValuesByField[field] ?? [];

  const setInValues = (field: MultiSelectFilterKey, values: string[]) => {
    const nextFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.field === field &&
          (filter.operator === FilterOperatorEnum.eq ||
            filter.operator === FilterOperatorEnum.in)
        ),
    );

    if (values.length === 1) {
      const [singleValue] = values;
      if (!singleValue) {
        throw new Error("Missing value for eq operator");
      }

      nextFilters.push({
        field,
        operator: FilterOperatorEnum.eq,
        value: singleValue,
      });
    } else if (values.length > 1) {
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.in,
        value: values,
      });
    }

    setFilters(nextFilters);
  };

  const isNullFilterActive = (field: NullableDateFilterKey) =>
    activeFilters.some(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.isNull,
    );

  const setNullFilter = (field: NullableDateFilterKey, active: boolean) => {
    const nextFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.field === field &&
          (active || filter.operator === FilterOperatorEnum.isNull)
        ),
    );
    if (active) {
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.isNull,
        value: "null",
      });
    }
    setFilters(nextFilters);
  };

  const isDateRangeInvalid = (filterKey: DateFilterKey) => {
    const gte = getFilterValue(filterKey, FilterOperatorEnum.gte);
    const lte = getFilterValue(filterKey, FilterOperatorEnum.lte);
    if (!gte || !lte) {
      return false;
    }

    if (
      !isFilterFieldValueValid(filterKey, gte) ||
      !isFilterFieldValueValid(filterKey, lte)
    ) {
      return false;
    }

    return new Date(gte) > new Date(lte);
  };

  const removeFilter = (field: FilterField) => {
    setFilters(activeFilters.filter((filter) => filter.field !== field));
  };

  return {
    dateFilterKeys: dateFields,
    multiSelectFilterKeys: multiSelectFields,
    nullableDateFilterKeys,
    multiSelectFieldData,
    activeFilters,
    getFilterValue,
    setFilterValue,
    getInValues,
    setInValues,
    isNullFilterActive,
    setNullFilter,
    isDateRangeInvalid,
    removeFilter,
  };
};
